import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useResident } from '../lib/useResident.jsx';
import { useAuth } from '../lib/useAuth.jsx';
import { useAdminAccess } from '../lib/useAdminAccess.jsx';
import {
  useCurrentContext,
  CONTEXT_TYPES,
} from '../lib/useCurrentContext.jsx';
import SignInMenu from './SignInMenu.jsx';
import './Layout.css';

// Masthead nav is context-aware:
//  - On admin pages: super-admin sees Communities + Catalog; community
//    admin sees just their roster link (Sign out always available)
//  - When a resident is signed in: My Dashboard + Sign out
//  - When an individual subscriber is signed in: My Account + Sign out
//  - On public pages with no active session: SignInMenu (3 options)
//
// The program band ("Brain Health Program") appears on every page
// EXCEPT the marketing home page, where it's redundant — the page
// itself prominently identifies what the program is.
//
// Branch ordering matters. The four cases above are evaluated top-down
// and the first match wins. A user can be in multiple states at once
// (e.g., a Quiltt staff member who is also an individual subscriber),
// so the order encodes precedence: admin-on-admin-page is most
// specific, then resident (localStorage-fast, no flicker), then
// individual (auth+RTDB, may flicker briefly on first paint), then
// the public sign-in menu as fallback.
export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isSignedIn: residentSignedIn, signOut: residentSignOut } = useResident();
  const {
    isSignedIn: adminSignedIn,
    isSuperAdmin,
    logout: authLogout,
  } = useAuth();
  const { communities: adminCommunities } = useAdminAccess();
  const { type: contextType } = useCurrentContext();

  const isAdminPage = location.pathname.startsWith('/admin')
    && !location.pathname.startsWith('/admin/login')
    && !location.pathname.startsWith('/admin/no-access');

  // The marketing landing page is its own visual surface; suppress
  // the program band there so the hero is the first thing visitors see.
  const isHomePage = location.pathname === '/';

  function handleResidentSignOut() {
    residentSignOut();
    navigate('/', { replace: true });
  }

  async function handleIndividualSignOut() {
    await authLogout();
    navigate('/', { replace: true });
  }

  return (
    <div className="layout">
      <header className="masthead">
        <div className="container masthead-inner">
          <Link to="/" className="wordmark" aria-label="Games by Quiltt — home">
            <img
              src="/games-logo.svg"
              alt=""
              className="wordmark-logo"
              width="44"
              height="44"
            />
            <span className="wordmark-text">
              <span className="wordmark-line-1">Games</span>
              <span className="wordmark-line-2">by Quiltt</span>
            </span>
          </Link>

          <nav className="masthead-nav" aria-label="Primary">
            {isAdminPage && adminSignedIn ? (
              <>
                {isSuperAdmin ? (
                  <>
                    <Link to="/admin/communities" className="nav-link">Communities</Link>
                    <Link to="/admin/catalog" className="nav-link">Catalog</Link>
                  </>
                ) : adminCommunities.length > 1 ? (
                  <Link to="/admin/picker" className="nav-link">Switch community</Link>
                ) : null}
              </>
            ) : residentSignedIn ? (
              <>
                <Link to="/resident" className="nav-link">My Dashboard</Link>
                <button
                  type="button"
                  className="nav-link nav-link-button"
                  onClick={handleResidentSignOut}
                >
                  Sign out
                </button>
              </>
            ) : contextType === CONTEXT_TYPES.INDIVIDUAL_USER ? (
              <>
                <Link to="/account" className="nav-link">My Account</Link>
                <button
                  type="button"
                  className="nav-link nav-link-button"
                  onClick={handleIndividualSignOut}
                >
                  Sign out
                </button>
              </>
            ) : (
              <SignInMenu />
            )}
          </nav>
        </div>
        <div className="masthead-rule" />
      </header>

      {!isHomePage ? (
        <div className="program-band">
          <div className="container">
            <span className="program-label">
              <span className="program-label-dot" aria-hidden="true" />
              Brain Health Program
            </span>
          </div>
        </div>
      ) : null}

      <main className={`page${isHomePage ? ' page--home' : ''}`}>
        <Outlet />
      </main>

      <footer className="colophon">
        <div className="container colophon-inner">
          <div className="colophon-left">
            <span className="colophon-tagline">
              An evidence-based cognitive fitness program.
            </span>
          </div>
          <div className="colophon-right">
            <span className="colophon-meta">
              In partnership with Dr. Rob Winningham,
              Western Oregon University.
            </span>
            <span className="colophon-meta colophon-copy">
              © {new Date().getFullYear()} Quiltt
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
